import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, getDocs, addDoc, deleteDoc, doc,
  query, where, writeBatch, updateDoc,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import AppDialog from '../components/AppDialog';

const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL || '';
const DELETE_URL = UPLOAD_URL.replace('upload.php', 'delete.php');

const MAX_SIZE_BYTES = 2 * 1024 * 1024;
const MAX_DIMENSION  = 2000;
const ALLOWED_TYPES  = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function validateImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION)
        reject(new Error(`Image is ${img.width}×${img.height} px — max allowed is ${MAX_DIMENSION}×${MAX_DIMENSION} px`));
      else resolve();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

async function getAuthToken() {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated — please log in again');
  return token;
}

async function uploadPoster(tournamentId, file) {
  const token = await getAuthToken();
  const form  = new FormData();
  form.append('poster', file);
  form.append('tournament_id', tournamentId);

  const res  = await fetch(UPLOAD_URL, {
    method:  'POST',
    headers: { Authorization: `Bearer ${token}` },
    body:    form,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Upload failed');
  return { url: json.poster_url, filename: json.filename };
}

async function deletePoster(filename) {
  if (!filename) return;
  const token = await getAuthToken();
  await fetch(DELETE_URL, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body:    JSON.stringify({ filename }),
  });
}

function filenameFromUrl(url) {
  if (!url) return null;
  try { return new URL(url).pathname.split('/').pop(); } catch { return null; }
}

function ApprovalBadge({ status }) {
  const map = {
    pending:  { bg: '#fff8e1', color: '#f39c12', label: 'Pending Approval' },
    approved: { bg: '#eafaf1', color: '#27ae60', label: 'Approved' },
    rejected: { bg: '#fdf2f2', color: '#e74c3c', label: 'Rejected' },
  };
  const cfg = map[status || 'pending'];
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
    }}>
      {cfg.label}
    </span>
  );
}

export default function Tournaments() {
  const { admin, adminProfile, isSuperAdmin } = useAuth();
  const [tournaments, setTournaments]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [form, setForm]                   = useState({ name: '', type: 'singles', format: 'round_robin', points_to_win: 11, sets_to_win: 1, start_date: '' });
  const [posterFile, setPosterFile]       = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [saving, setSaving]               = useState(false);
  const [dialog, setDialog]               = useState(null);
  const fileInputRef                      = useRef(null);

  const showAlert   = (title, message) => setDialog({ title, message });
  const showConfirm = (title, message, onConfirm) =>
    setDialog({ title, message, onConfirm, danger: true, confirmText: 'Delete', cancelText: 'Cancel' });

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'tournaments'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.created_at?.localeCompare(a.created_at));

      // Super admin sees everything; regular admins see only their own
      const visible = isSuperAdmin
        ? all
        : all.filter(t => t.admin_uid === admin.uid);

      setTournaments(visible);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // ── Poster file selection ────────────────────────────────────────────────────
  const handlePosterChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      showAlert('Invalid File Type', 'Only JPEG, PNG, WebP, and GIF images are allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      showAlert('File Too Large', 'Maximum poster size is 2 MB.');
      e.target.value = '';
      return;
    }
    try { await validateImageDimensions(file); } catch (err) {
      showAlert('Image Too Large', err.message);
      e.target.value = '';
      return;
    }
    setPosterFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPosterPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearPoster = () => {
    setPosterFile(null);
    setPosterPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Create tournament ────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Super admin's own tournaments are auto-approved
      const approvalStatus = isSuperAdmin ? 'approved' : 'pending';

      const ref = await addDoc(collection(db, 'tournaments'), {
        name:            form.name,
        type:            form.type,
        format:          form.format || 'round_robin',
        points_to_win:   form.points_to_win || 11,
        sets_to_win:     form.sets_to_win || 1,
        status:          'setup',
        start_date:      form.start_date || null,
        poster_url:      null,
        admin_uid:       admin.uid,
        admin_email:     admin.email,
        approval_status: approvalStatus,
        approved_at:     isSuperAdmin ? new Date().toISOString() : null,
        approved_by_uid: isSuperAdmin ? admin.uid : null,
        created_at:      new Date().toISOString(),
      });

      if (posterFile) {
        try {
          const { url } = await uploadPoster(ref.id, posterFile);
          await updateDoc(ref, { poster_url: url });
        } catch (uploadErr) {
          showAlert('Poster Upload Failed', `Tournament created, but poster upload failed: ${uploadErr.message}`);
        }
      }

      await load();
      setShowModal(false);
      setForm({ name: '', type: 'singles', format: 'round_robin', points_to_win: 11, sets_to_win: 1, start_date: '' });
      clearPoster();
    } catch (err) {
      showAlert('Error', err.message || 'Error creating tournament');
    } finally { setSaving(false); }
  };

  // ── Delete tournament ────────────────────────────────────────────────────────
  const handleDelete = (t) => {
    showConfirm('Delete Tournament', 'Delete this tournament and all its data? This cannot be undone.', async () => {
      const batch = writeBatch(db);

      const matchesSnap = await getDocs(query(collection(db, 'matches'), where('tournament_id', '==', t.id)));
      for (const matchDoc of matchesSnap.docs) {
        const pointsSnap = await getDocs(query(collection(db, 'points'), where('match_id', '==', matchDoc.id)));
        pointsSnap.docs.forEach(p => batch.delete(p.ref));
        batch.delete(matchDoc.ref);
      }

      const teamsSnap  = await getDocs(query(collection(db, 'teams'),  where('tournament_id', '==', t.id)));
      const groupsSnap = await getDocs(query(collection(db, 'groups'), where('tournament_id', '==', t.id)));
      teamsSnap.docs.forEach(tm  => batch.delete(tm.ref));
      groupsSnap.docs.forEach(g  => batch.delete(g.ref));
      batch.delete(doc(db, 'tournaments', t.id));
      await batch.commit();

      await deletePoster(filenameFromUrl(t.poster_url));
      load();
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setForm({ name: '', type: 'singles', format: 'round_robin', points_to_win: 11, sets_to_win: 1, start_date: '' });
    clearPoster();
  };

  return (
    <div className="container" style={{ padding: '32px 20px' }}>
      <div className="page-header">
        <div>
          <h1>Tournaments</h1>
          <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '4px' }}>
            {isSuperAdmin ? `${tournaments.length} total (all admins)` : `${tournaments.length} total`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Tournament</button>
      </div>

      {!isSuperAdmin && (
        <div style={s.infoBox}>
          New tournaments require super admin approval before matches can be started. You will be able to set up
          teams and schedules while awaiting approval.
        </div>
      )}

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-fadein" style={{ padding: 0, overflow: 'hidden', animationDelay: `${i * 0.05}s` }}>
              <div className="skeleton" style={{ height: '140px', borderRadius: 0 }} />
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="skeleton" style={{ height: '18px', width: '70%' }} />
                <div className="skeleton" style={{ height: '14px', width: '50%' }} />
                <div className="skeleton" style={{ height: '32px', marginTop: '8px' }} />
              </div>
            </div>
          ))}
        </div>
      ) : tournaments.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon">🏆</div>
            <h3>No tournaments yet</h3>
            <p>Create your first tournament to get started</p>
            <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowModal(true)}>
              Create Tournament
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {tournaments.map((t, i) => (
            <div key={t.id} className="card animate-fadein" style={{ animationDelay: `${i * 0.05}s`, position: 'relative', padding: 0, overflow: 'hidden' }}>
              {t.poster_url ? (
                <div style={{ height: '140px', overflow: 'hidden', background: '#f0f4f8' }}>
                  <img src={t.poster_url} alt={t.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                </div>
              ) : (
                <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f0f4f8 0%, #e8f5e9 100%)' }}>
                  <span style={{ fontSize: '36px' }}>🏆</span>
                </div>
              )}
              <div style={{ padding: '16px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-12px', right: '16px' }}>
                  <span className={`badge badge-${t.status}`}>{t.status}</span>
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '6px', paddingRight: '60px' }}>{t.name}</h3>

                {/* Approval status badge */}
                <div style={{ marginBottom: '8px' }}>
                  <ApprovalBadge status={t.approval_status} />
                </div>

                {isSuperAdmin && t.admin_email && (
                  <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '6px' }}>
                    By: {t.admin_email}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <span className={`badge badge-${t.type}`}>{t.type}</span>
                  <span className="tag">{t.format?.replace(/_/g, ' ')}</span>
                  <span className="tag">to {t.points_to_win} pts</span>
                </div>
                {t.start_date && (
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>
                    📅 {new Date(t.start_date).toLocaleDateString()}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f4f8' }}>
                  <Link to={`/tournaments/${t.id}`} className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: 'center' }}>Manage</Link>
                  <button className="btn btn-ghost btn-sm" style={{ color: '#e74c3c' }} onClick={() => handleDelete(t)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && handleCloseModal()}>
          <div className="modal animate-fadein">
            <h2>Create Tournament</h2>
            {!isSuperAdmin && (
              <div style={{ ...s.infoBox, marginBottom: '16px', fontSize: '13px' }}>
                This tournament will require super admin approval before matches can be started.
              </div>
            )}
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Tournament Name *</label>
                <input className="form-control" placeholder="e.g. Summer Open 2024"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Type</label>
                  <select className="form-control" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="singles">Singles</option>
                    <option value="doubles">Doubles</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Format</label>
                  <select className="form-control" value={form.format} onChange={e => setForm(f => ({ ...f, format: e.target.value }))}>
                    <option value="round_robin">Round Robin</option>
                    <option value="single_elimination">Single Elimination</option>
                    <option value="group_knockout">Group + Knockout</option>
                  </select>
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Points to Win</label>
                  <select className="form-control" value={form.points_to_win} onChange={e => setForm(f => ({ ...f, points_to_win: +e.target.value }))}>
                    <option value={11}>11</option><option value={15}>15</option><option value={21}>21</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sets per Match</label>
                  <select className="form-control" value={form.sets_to_win} onChange={e => setForm(f => ({ ...f, sets_to_win: +e.target.value }))}>
                    <option value={1}>Single Set</option><option value={2}>Best of 3</option><option value={3}>Best of 5</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input className="form-control" type="date" value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>

              <div className="form-group">
                <label>Tournament Poster / Logo <span style={{ fontWeight: 400, color: '#7f8c8d' }}>(optional)</span></label>
                {posterPreview ? (
                  <div style={{ position: 'relative', marginBottom: '8px' }}>
                    <img src={posterPreview} alt="Preview"
                      style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
                    <button type="button" onClick={clearPoster}
                      style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '13px', lineHeight: '24px', textAlign: 'center' }}>
                      ✕
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', border: '2px dashed #d0d7de', borderRadius: '8px', padding: '20px', cursor: 'pointer', background: '#fafafa', color: '#7f8c8d', fontSize: '13px' }}>
                    <span style={{ fontSize: '28px' }}>🖼</span>
                    Click to select image
                    <span style={{ fontSize: '11px' }}>JPEG, PNG, WebP or GIF · max 2 MB · max {MAX_DIMENSION}×{MAX_DIMENSION} px</span>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                      style={{ display: 'none' }} onChange={handlePosterChange} />
                  </label>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Tournament'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AppDialog dialog={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}

const s = {
  infoBox: {
    background: '#fff8e1', border: '1px solid #f39c12', borderRadius: '8px',
    padding: '10px 14px', color: '#856404', fontSize: '13px', marginBottom: '16px',
    lineHeight: '1.5',
  },
};
